import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  ScrollView,
  Switch,
  TextInput,
  View
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import DefaultScreen from '@components/Global/DefaultScreenView';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@constants/Themed';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import {
  Button,
  H2,
  H3,
  H4,
  H6,
  SizableText,
  XStack,
  YStack
} from 'tamagui';
import { useQuery } from '@tanstack/react-query';
import { useStripeConnection } from '@hooks/useStripeConnection';

interface StoreSettingsState {
  currency: string;
  currencySymbol: string;
  timezone: string;
  defaultCategory: 'products' | 'services' | 'all';
  showOutOfStock: boolean;
  lowStockThreshold: number;
  storefrontMessage: string;
  enableTax: boolean;
  taxRate: number;
  allowGuestCheckout: boolean;
  paymentProvider?: 'stripe' | 'paystack' | null;
}

interface CurrencyOption {
  code: string;
  symbol: string;
  label: string;
}

const DEFAULT_SETTINGS: StoreSettingsState = {
  currency: 'USD',
  currencySymbol: '$',
  timezone: 'UTC',
  defaultCategory: 'products',
  showOutOfStock: true,
  lowStockThreshold: 5,
  storefrontMessage: '',
  enableTax: false,
  taxRate: 0,
  allowGuestCheckout: true,
  paymentProvider: null
};

const CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: 'USD', symbol: '$', label: 'US Dollar (USD)' },
  { code: 'GBP', symbol: '£', label: 'British Pound (GBP)' },
  { code: 'EUR', symbol: '€', label: 'Euro (EUR)' },
  { code: 'CAD', symbol: '$', label: 'Canadian Dollar (CAD)' },
  { code: 'AUD', symbol: '$', label: 'Australian Dollar (AUD)' },
  { code: 'JPY', symbol: '¥', label: 'Japanese Yen (JPY)' }
];

const CATEGORY_OPTIONS: Array<{ value: StoreSettingsState['defaultCategory']; label: string; icon: keyof typeof MaterialIcons.glyphMap }> = [
  { value: 'products', label: 'Products', icon: 'shopping-bag' },
  { value: 'services', label: 'Services', icon: 'handyman' },
  { value: 'all', label: 'Products & Services', icon: 'all-inbox' }
];

const StoreSettings: React.FC = () => {
  const userData = useSelector((state: DefaultRootState) => state.promiseData?.data?.userData);
  const userId = userData?._id || userData?.id;
  const theme = themeSelector();
  const navigation = useNavigation();
  const { showConfirm } = useAlertDialog();

  const [settings, setSettings] = useState<StoreSettingsState>(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState<StoreSettingsState>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Check Stripe connection
  const stripeConnection = useStripeConnection();
  const hasStripe = stripeConnection.canProcessPayments;

  // Check PayStack connection
  const { data: paystackProfile } = useQuery({
    queryKey: ['paystackProfile'],
    queryFn: async () => {
      try {
        return await apis.getPayStackProfile();
      } catch (error: any) {
        // If 400 error means "keys not found", return not connected
        if (error?.response?.status === 400 && error?.response?.data?.error === 'PayStack keys not found') {
          return { authorised: false, profile: null };
        }
        return { authorised: false, profile: null };
      }
    },
    enabled: true,
    staleTime: 1000 * 60 * 5,
    retry: false
  });
  const hasPayStack = paystackProfile && (paystackProfile.authorised === true || paystackProfile.profile);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchSettings = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apis.getStoreSettings(userId);
        if (!isMounted) {
          return;
        }
        // Handle response - API function already extracts data, but ensure we handle empty responses
        if (!response || (typeof response === 'object' && Object.keys(response).length === 0)) {
          // If no settings exist, use defaults
          setSettings(DEFAULT_SETTINGS);
          setInitialSettings(DEFAULT_SETTINGS);
        } else {
          const merged = { ...DEFAULT_SETTINGS, ...(response || {}) } as StoreSettingsState;
          setSettings(merged);
          setInitialSettings(merged);
        }
      } catch (err: any) {
        console.error('[StoreSettings] Failed to load settings:', err);
        if (isMounted) {
          // Handle different error formats
          const errorMessage = err?.response?.data?.error
            || err?.response?.data?.message
            || err?.message
            || 'Unable to load store settings';
          setError(errorMessage);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  const hasChanges = useMemo(() => {
    return JSON.stringify(settings) !== JSON.stringify(initialSettings);
  }, [settings, initialSettings]);

  // Handle navigation away with unsaved changes
  useEffect(() => {
    const handleBeforeRemove = async (e: any) => {
      if (!hasChanges) {
        // No unsaved changes, allow navigation
        return;
      }

      // Prevent default navigation
      e.preventDefault();

      try {
        // Show confirmation dialog
        const shouldDiscard = await showConfirm(
          'Unsaved Changes',
          'You have unsaved changes. Are you sure you want to discard them and leave?',
          {
            confirmText: 'Discard Changes',
            cancelText: 'Stay'
          }
        );

        // showConfirm returns true if user confirms, false if cancelled
        if (shouldDiscard === true) {
          // User confirmed, allow navigation
          navigation.dispatch(e.data.action);
        }
        // If user cancelled (shouldDiscard === false), navigation is already prevented
      } catch (error) {
        // If dialog fails, prevent navigation as a safety measure
        console.error('[StoreSettings] Error showing unsaved changes dialog:', error);
      }
    };

    const unsubscribe = navigation.addListener('beforeRemove', handleBeforeRemove);

    return unsubscribe;
  }, [navigation, hasChanges, showConfirm]);

  const handleCurrencySelect = useCallback((option: CurrencyOption) => {
    setSettings(prev => ({
      ...prev,
      currency: option.code,
      currencySymbol: option.symbol
    }));
  }, []);

  const handleBooleanChange = useCallback((key: 'showOutOfStock' | 'enableTax' | 'allowGuestCheckout') => (value: boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const handleCategoryChange = useCallback((value: StoreSettingsState['defaultCategory']) => {
    setSettings(prev => ({
      ...prev,
      defaultCategory: value
    }));
  }, []);

  const handleLowStockChange = useCallback((value: string) => {
    const numeric = Number.parseInt(value.replace(/[^0-9]/g, ''), 10);
    setSettings(prev => ({
      ...prev,
      lowStockThreshold: Number.isNaN(numeric) ? 0 : Math.max(0, Math.min(numeric, 100000))
    }));
  }, []);

  const handleTaxRateChange = useCallback((value: string) => {
    const sanitized = value.replace(/[^0-9.]/g, '');
    const numeric = Number.parseFloat(sanitized);
    setSettings(prev => ({
      ...prev,
      taxRate: Number.isNaN(numeric) ? 0 : Math.max(0, Math.min(Number.parseFloat(numeric.toFixed(2)), 100))
    }));
  }, []);

  const handleTextChange = useCallback((key: 'timezone' | 'storefrontMessage') => (value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  const handlePaymentProviderChange = useCallback((provider: 'stripe' | 'paystack' | null) => {
    setSettings(prev => ({
      ...prev,
      paymentProvider: provider
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!userId || saving || !hasChanges) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updated = await apis.updateStoreSettings(userId, settings);
      // Handle response - API function already extracts data
      const merged = { ...DEFAULT_SETTINGS, ...(updated || {}) } as StoreSettingsState;
      setSettings(merged);
      setInitialSettings(merged);
      Alert.alert('Store settings updated', 'Your preferences have been saved successfully.');
    } catch (err: any) {
      console.error('[StoreSettings] Failed to update settings:', err);
      // Handle different error formats
      const errorMessage = err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Unable to update store settings';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  }, [userId, saving, hasChanges, settings]);

  const renderSwitch = (value: boolean, onChange: (val: boolean) => void) => (
    <Switch
      value={value}
      onValueChange={onChange}
      trackColor={{ false: theme ? '#cbd5e1' : '#334155', true: theme ? '#0ea5e9' : '#38bdf8' }}
      thumbColor={theme ? '#ffffff' : '#f8fafc'}
    />
  );

  const inputStyle = useMemo(() => ({
    borderColor: theme ? '#cbd5e1' : '#475569',
    backgroundColor: theme ? '#ffffff' : '#0f172a',
    color: theme ? '#0f172a' : '#f8fafc',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14
  }), [theme]);

  if (!userId) {
    return (
      <DefaultScreen noMargin>
        <YStack flex={1} justifyContent="center" alignItems="center" padding="$6">
          <View
            style={{
              width: '90%',
              maxWidth: 420,
              backgroundColor: theme ? '#ffffff' : '#1e293b',
              borderRadius: 20,
              padding: 24,
              alignItems: 'center',
              borderWidth: 1,
              borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'
            }}
          >
            <MaterialIcons name="store" size={48} color={theme ? '#0ea5e9' : '#38bdf8'} />
            <H3 style={{ color: theme ? '#1e293b' : '#f1f5f9', textAlign: 'center', marginTop: 16 }}>
              Store not found
            </H3>
            <Text style={{ color: theme ? '#64748b' : '#94a3b8', textAlign: 'center', fontSize: 14, marginTop: 8 }}>
              You need an active store profile before you can configure storefront preferences.
            </Text>
          </View>
        </YStack>
      </DefaultScreen>
    );
  }

  if (loading) {
    return (
      <DefaultScreen noMargin>
        <YStack flex={1} justifyContent="center" alignItems="center" padding="$6">
          <ActivityIndicator size="large" color={theme ? '#0ea5e9' : '#38bdf8'} />
          <Text style={{ color: theme ? '#475569' : '#94a3b8', marginTop: 12, fontSize: 14 }}>
            Loading store settings…
          </Text>
        </YStack>
      </DefaultScreen>
    );
  }

  return (
    <DefaultScreen style={{ alignSelf: "stretch", gap: 24, paddingBottom: 50 }} containerStyle={{ alignSelf: "stretch", paddingBottom: 80 }}>
      <View style={{ flex: 1 }}>
        <ScrollView
          contentContainerStyle={{ padding: 24, paddingBottom: 140 }}
          showsVerticalScrollIndicator={false}
        >
          <YStack gap={24}>
            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                padding: 24,
                width: '100%',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)'
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: error ? 20 : 0 }}>
                <View
                  style={{
                    backgroundColor: theme ? '#ddd6fe' : '#312e81',
                    borderRadius: 12,
                    padding: 12,
                    marginRight: 16
                  }}
                >
                  <MaterialIcons
                    name="storefront"
                    size={28}
                    color={theme ? '#7c3aed' : '#a78bfa'}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <H6 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700',
                    marginBottom: 4
                  }}>
                    Store Settings
                  </H6>
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14
                  }}>
                    Manage how customers experience your storefront across catalog, pricing and checkout.
                  </Text>
                </View>
              </View>
              {error ? (
                <View
                  style={{
                    backgroundColor: theme ? '#fee2e2' : '#7f1d1d',
                    borderRadius: 16,
                    padding: 12,
                    marginTop: 16,
                    borderWidth: 1,
                    borderColor: theme ? '#fca5a5' : '#991b1b'
                  }}
                >
                  <XStack alignItems="center" gap="$2">
                    <MaterialIcons name="error" size={20} color={theme ? '#b91c1c' : '#fecaca'} />
                    <Text style={{ color: theme ? '#b91c1c' : '#fecaca', fontSize: 14 }}>
                      {error}
                    </Text>
                  </XStack>
                </View>
              ) : null}
            </View>

            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? '#fef3c7' : '#451a03',
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons name="payments" size={20} color={theme ? '#f59e0b' : '#fbbf24'} />
                  </View>
                  <H4 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700'
                  }}>
                    Currency & pricing
                  </H4>
                </View>
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                  marginTop: 4
                }}>
                  Choose the currency applied across your storefront and pricing calculations.
                </Text>
              </View>
              <View style={{ padding: 20 }}>

                <YStack gap={12}>
                  {CURRENCY_OPTIONS.map(option => {
                    const isSelected = settings.currency === option.code;
                    return (
                      <Button
                        key={option.code}
                        backgroundColor={isSelected ? (theme ? '#dbeafe' : '#0c4a6e') : (theme ? '#f8fafc' : '#334155')}
                        borderColor={isSelected ? (theme ? '#0ea5e9' : '#38bdf8') : 'transparent'}
                        borderRadius={16}
                        padding="$3"
                        justifyContent="flex-start"
                        onPress={() => handleCurrencySelect(option)}
                      >
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons
                            name={isSelected ? 'radio-button-checked' : 'radio-button-unchecked'}
                            size={20}
                            color={isSelected ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#64748b' : '#94a3b8')}
                          />
                          <YStack>
                            <SizableText
                              size="$4"
                              color={isSelected ? (theme ? '#0f172a' : '#f8fafc') : (theme ? '#1f2937' : '#f8fafc')}
                              fontWeight="600"
                            >
                              {option.symbol} {option.code}
                            </SizableText>
                            <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                              {option.label}
                            </SizableText>
                          </YStack>
                        </XStack>
                      </Button>
                    );
                  })}
                </YStack>
              </View>
            </View>

            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? '#d1fae5' : '#064e3b',
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons name="inventory" size={20} color={theme ? '#059669' : '#10b981'} />
                  </View>
                  <H4 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700'
                  }}>
                    Inventory visibility
                  </H4>
                </View>
              </View>
              <View style={{ padding: 20 }}>
                <YStack gap={12}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1} marginRight="$3">
                      <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                        Show out of stock items
                      </SizableText>
                      <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                        Keep unavailable products visible with an out-of-stock indicator.
                      </SizableText>
                    </YStack>
                    {renderSwitch(settings.showOutOfStock, handleBooleanChange('showOutOfStock'))}
                  </XStack>
                  <YStack gap="$2">
                    <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                      Low stock threshold
                    </SizableText>
                    <TextInput
                      value={String(settings.lowStockThreshold)}
                      onChangeText={handleLowStockChange}
                      keyboardType="number-pad"
                      style={inputStyle}
                    />
                    <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                      We'll highlight items when inventory drops below this quantity.
                    </SizableText>
                  </YStack>
                </YStack>
              </View>
            </View>

            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? '#f0f9ff' : '#0f172a',
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons name="view-carousel" size={20} color={theme ? '#0ea5e9' : '#38bdf8'} />
                  </View>
                  <H4 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700'
                  }}>
                    Catalog defaults
                  </H4>
                </View>
                <Text style={{
                  color: theme ? '#64748b' : '#94a3b8',
                  fontSize: 14,
                  marginTop: 4
                }}>
                  Choose what visitors see first when they land on your storefront.
                </Text>
              </View>
              <View style={{ padding: 20 }}>

                <YStack gap={12}>
                  {CATEGORY_OPTIONS.map(option => {
                    const isSelected = settings.defaultCategory === option.value;
                    return (
                      <Button
                        key={option.value}
                        backgroundColor={isSelected ? (theme ? '#dcfce7' : '#064e3b') : (theme ? '#f8fafc' : '#334155')}
                        borderColor={isSelected ? (theme ? '#059669' : '#10b981') : 'transparent'}
                        borderRadius={16}
                        padding="$3"
                        justifyContent="flex-start"
                        onPress={() => handleCategoryChange(option.value)}
                      >
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons
                            name={option.icon}
                            size={20}
                            color={isSelected ? (theme ? '#059669' : '#10b981') : (theme ? '#64748b' : '#94a3b8')}
                          />
                          <SizableText
                            size="$4"
                            color={isSelected ? (theme ? '#0f172a' : '#f8fafc') : (theme ? '#1f2937' : '#f8fafc')}
                            fontWeight="600"
                          >
                            {option.label}
                          </SizableText>
                        </XStack>
                      </Button>
                    );
                  })}
                </YStack>
              </View>
            </View>

            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? '#dcfce7' : '#14532d',
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons name="shopping-cart-checkout" size={20} color={theme ? '#16a34a' : '#22c55e'} />
                  </View>
                  <H4 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700'
                  }}>
                    Checkout & tax
                  </H4>
                </View>
              </View>
              <View style={{ padding: 20 }}>
                <YStack gap={12}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1} marginRight="$3">
                      <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                        Allow guest checkout
                      </SizableText>
                      <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                        Let shoppers place orders without creating an account.
                      </SizableText>
                    </YStack>
                    {renderSwitch(settings.allowGuestCheckout, handleBooleanChange('allowGuestCheckout'))}
                  </XStack>
                  <View style={{
                    height: 1,
                    backgroundColor: theme ? '#e2e8f0' : '#334155',
                    marginVertical: 12
                  }} />
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1} marginRight="$3">
                      <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                        Enable tax
                      </SizableText>
                      <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                        Automatically apply a default sales tax to every order.
                      </SizableText>
                    </YStack>
                    {renderSwitch(settings.enableTax, handleBooleanChange('enableTax'))}
                  </XStack>
                  {settings.enableTax ? (
                    <YStack gap="$2">
                      <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                        Default tax rate (%)
                      </SizableText>
                      <TextInput
                        value={String(settings.taxRate)}
                        onChangeText={handleTaxRateChange}
                        keyboardType="decimal-pad"
                        style={inputStyle}
                      />
                      <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                        Applied when products don't override this rate.
                      </SizableText>
                    </YStack>
                  ) : null}
                </YStack>
              </View>
            </View>

            {/* Payment Provider Selection */}
            {(hasStripe || hasPayStack) && (
              <View
                style={{
                  backgroundColor: theme ? '#ffffff' : '#1e293b',
                  borderRadius: 20,
                  borderWidth: 1,
                  borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                  overflow: 'hidden'
                }}
              >
                <View style={{
                  padding: 20,
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? '#e2e8f0' : '#334155'
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <View style={{
                      backgroundColor: theme ? '#fef3c7' : '#451a03',
                      borderRadius: 8,
                      padding: 8,
                      marginRight: 12
                    }}>
                      <MaterialIcons name="payment" size={20} color={theme ? '#f59e0b' : '#fbbf24'} />
                    </View>
                    <H4 style={{
                      color: theme ? '#1e293b' : '#f1f5f9',
                      fontWeight: '700'
                    }}>
                      Payment Provider
                    </H4>
                  </View>
                  <Text style={{
                    color: theme ? '#64748b' : '#94a3b8',
                    fontSize: 14,
                    marginTop: 4
                  }}>
                    Choose which payment provider to use for processing store transactions.
                  </Text>
                </View>
                <View style={{ padding: 20 }}>
                  <YStack gap={12}>
                    {/* None option */}
                    <Button
                      backgroundColor={settings.paymentProvider === null ? (theme ? '#dbeafe' : '#0c4a6e') : (theme ? '#f8fafc' : '#334155')}
                      borderColor={settings.paymentProvider === null ? (theme ? '#0ea5e9' : '#38bdf8') : 'transparent'}
                      borderWidth={settings.paymentProvider === null ? 2 : 0}
                      borderRadius={16}
                      padding="$3"
                      justifyContent="flex-start"
                      onPress={() => handlePaymentProviderChange(null)}
                    >
                      <XStack alignItems="center" gap="$2">
                        <MaterialIcons
                          name={settings.paymentProvider === null ? 'radio-button-checked' : 'radio-button-unchecked'}
                          size={20}
                          color={settings.paymentProvider === null ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#64748b' : '#94a3b8')}
                        />
                        <SizableText
                          size="$4"
                          color={settings.paymentProvider === null ? (theme ? '#0f172a' : '#f8fafc') : (theme ? '#1f2937' : '#f8fafc')}
                          fontWeight="600"
                        >
                          None (Manual payment)
                        </SizableText>
                      </XStack>
                    </Button>

                    {/* Stripe option */}
                    {hasStripe && (
                      <Button
                        backgroundColor={settings.paymentProvider === 'stripe' ? (theme ? '#dbeafe' : '#0c4a6e') : (theme ? '#f8fafc' : '#334155')}
                        borderColor={settings.paymentProvider === 'stripe' ? (theme ? '#0ea5e9' : '#38bdf8') : 'transparent'}
                        borderWidth={settings.paymentProvider === 'stripe' ? 2 : 0}
                        borderRadius={16}
                        padding="$3"
                        justifyContent="flex-start"
                        onPress={() => handlePaymentProviderChange('stripe')}
                      >
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons
                            name={settings.paymentProvider === 'stripe' ? 'radio-button-checked' : 'radio-button-unchecked'}
                            size={20}
                            color={settings.paymentProvider === 'stripe' ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#64748b' : '#94a3b8')}
                          />
                          <XStack alignItems="center" gap="$2" flex={1}>
                            <MaterialIcons name="account-balance-wallet" size={20} color={theme ? '#635bff' : '#818cf8'} />
                            <YStack flex={1}>
                              <SizableText
                                size="$4"
                                color={settings.paymentProvider === 'stripe' ? (theme ? '#0f172a' : '#f8fafc') : (theme ? '#1f2937' : '#f8fafc')}
                                fontWeight="600"
                              >
                                Stripe
                              </SizableText>
                              <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                                Process payments via Stripe
                              </SizableText>
                            </YStack>
                          </XStack>
                        </XStack>
                      </Button>
                    )}

                    {/* PayStack option */}
                    {hasPayStack && (
                      <Button
                        backgroundColor={settings.paymentProvider === 'paystack' ? (theme ? '#dbeafe' : '#0c4a6e') : (theme ? '#f8fafc' : '#334155')}
                        borderColor={settings.paymentProvider === 'paystack' ? (theme ? '#0ea5e9' : '#38bdf8') : 'transparent'}
                        borderWidth={settings.paymentProvider === 'paystack' ? 2 : 0}
                        borderRadius={16}
                        padding="$3"
                        justifyContent="flex-start"
                        onPress={() => handlePaymentProviderChange('paystack')}
                      >
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons
                            name={settings.paymentProvider === 'paystack' ? 'radio-button-checked' : 'radio-button-unchecked'}
                            size={20}
                            color={settings.paymentProvider === 'paystack' ? (theme ? '#0ea5e9' : '#38bdf8') : (theme ? '#64748b' : '#94a3b8')}
                          />
                          <XStack alignItems="center" gap="$2" flex={1}>
                            <MaterialIcons name="credit-card" size={20} color="#00D9FF" />
                            <YStack flex={1}>
                              <SizableText
                                size="$4"
                                color={settings.paymentProvider === 'paystack' ? (theme ? '#0f172a' : '#f8fafc') : (theme ? '#1f2937' : '#f8fafc')}
                                fontWeight="600"
                              >
                                PayStack
                              </SizableText>
                              <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                                Process payments via PayStack
                              </SizableText>
                            </YStack>
                          </XStack>
                        </XStack>
                      </Button>
                    )}

                    {!hasStripe && !hasPayStack && (
                      <View style={{
                        backgroundColor: theme ? '#fef3c7' : '#451a03',
                        borderRadius: 12,
                        padding: 12,
                        borderWidth: 1,
                        borderColor: theme ? '#fde68a' : '#78350f'
                      }}>
                        <XStack alignItems="center" gap="$2">
                          <MaterialIcons name="info" size={20} color={theme ? '#f59e0b' : '#fbbf24'} />
                          <SizableText size="$3" color={theme ? '#92400e' : '#fcd34d'}>
                            No payment providers connected. Connect Stripe or PayStack in Integrations to enable payment processing.
                          </SizableText>
                        </XStack>
                      </View>
                    )}
                  </YStack>
                </View>
              </View>
            )}

            <View
              style={{
                backgroundColor: theme ? '#ffffff' : '#1e293b',
                borderRadius: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.1)',
                overflow: 'hidden'
              }}
            >
              <View style={{
                padding: 20,
                borderBottomWidth: 1,
                borderBottomColor: theme ? '#e2e8f0' : '#334155'
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <View style={{
                    backgroundColor: theme ? '#dbeafe' : '#0c4a6e',
                    borderRadius: 8,
                    padding: 8,
                    marginRight: 12
                  }}>
                    <MaterialIcons name="schedule" size={20} color={theme ? '#0ea5e9' : '#38bdf8'} />
                  </View>
                  <H4 style={{
                    color: theme ? '#1e293b' : '#f1f5f9',
                    fontWeight: '700'
                  }}>
                    Storefront details
                  </H4>
                </View>
              </View>
              <View style={{ padding: 20 }}>
                <YStack gap={12}>
                  <YStack gap="$2">
                    <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                      Store timezone
                    </SizableText>
                    <TextInput
                      placeholder="e.g. Europe/London"
                      placeholderTextColor={theme ? '#94a3b8' : '#475569'}
                      value={settings.timezone}
                      onChangeText={handleTextChange('timezone')}
                      style={inputStyle}
                    />
                  </YStack>
                  <YStack gap="$2">
                    <SizableText size="$4" color={theme ? '#1e293b' : '#f8fafc'} fontWeight="600">
                      Storefront message
                    </SizableText>
                    <TextInput
                      placeholder="Welcome back! We now offer same-day delivery."
                      placeholderTextColor={theme ? '#94a3b8' : '#475569'}
                      value={settings.storefrontMessage}
                      onChangeText={handleTextChange('storefrontMessage')}
                      style={{
                        ...inputStyle,
                        height: 120,
                        textAlignVertical: 'top'
                      }}
                      multiline
                      numberOfLines={4}
                      maxLength={500}
                    />
                    <SizableText size="$3" color={theme ? '#64748b' : '#94a3b8'}>
                      Displayed at the top of your store to share announcements with shoppers.
                    </SizableText>
                  </YStack>
                </YStack>
              </View>
            </View>
          </YStack>
        </ScrollView>

        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            padding: 16,
            backgroundColor: theme ? '#ffffff' : '#0f172a',
            borderTopWidth: 1,
            borderColor: theme ? '#e2e8f0' : '#334155'
          }}
        >
          <Button
            backgroundColor={hasChanges && !saving ? (theme ? '#2563eb' : '#2563eb') : (theme ? '#94a3b8' : '#475569')}
            color="white"
            borderRadius={16}
            paddingVertical="$3"
            disabled={!hasChanges || saving}
            onPress={handleSave}
          >
            {saving ? (
              <ActivityIndicator color="#ffffff" />
            ) : (
              <XStack alignItems="center" gap="$2" justifyContent="center">
                <MaterialIcons name="save" size={20} color="white" />
                <SizableText size="$4" color="white" fontWeight="600">
                  Save changes
                </SizableText>
              </XStack>
            )}
          </Button>
        </View>
      </View>
    </DefaultScreen>
  );
};

export default StoreSettings;
